import React, { useEffect, useState } from "react"
import "./index.scss"
import FilterBar from "../FilterBar"
import JobCard from "../Cards/JobCard"
import PressCard from "../Cards/PressCard"
import PersonCard from "../Cards/PersonCard"
import UpdateCard from "../Cards/UpdateCard"
import { graphql, useStaticQuery } from "gatsby"
import ResourceCard from "../Cards/ResourceCard"

const cardSelector = (contentType, data) => {
  switch (contentType) {
    case "staff":
      return data?.map(person => (
        <PersonCard key={person.name} person={person} />
      ))

    case "job":
      return data?.map(job => <JobCard key={job.title} job={job} />)

    case "update":
      return data?.map(update => (
        <UpdateCard key={update.title} update={update} />
      ))

    case "resource":
      return data?.map(resource => (
        <ResourceCard key={resource.title} postData={resource} />
      ))

    case "press":
      return data?.map(article => (
        <PressCard key={article.headline} article={article} />
      ))

    default:
      break
  }
}

export default function ContentGrid({ location, component }) {
  // = = = = = = = = = = = = =
  // Data
  const { tagToShow, contentType, showFilterBar, contentFilter, resultsLimit } =
    component

  // = = = = = = = = = = = = =
  // States
  const [filters, setFilters] = useState([])
  const [dataSlice, setDataSlice] = useState(resultsLimit)
  const [initialContentData, setInitialContentData] = useState([])
  const [selectFiltersValues, setSelectFiltersValues] = useState([])
  const [filteredSingleContentData, setFilteredSingleContentData] = useState([])

  // = = = = = = = = = = = = =
  // GraphQL

  const allContentData = useStaticQuery(graphql`
    query MyQuery {
      staff: allDatoCmsStaff(sort: { fields: position, order: ASC }) {
        nodes {
          gridImage {
            gatsbyImageData(placeholder: BLURRED)
          }
          hiringSquare
          introduction
          jobPosition
          linkToJobPage
          profileImage {
            gatsbyImageData(placeholder: BLURRED)
          }
          position
          name
          slug
          location
          contentTag
          pronouns
          socialMedia {
            socialMediaItem {
              socialMediaId
              socialMediaLink
            }
          }
        }
      }
      job: allDatoCmsJob(sort: { fields: datePosted, order: DESC }) {
        nodes {
          datePosted(formatString: "MMMM DD, YYYY")
          description
          organization
          slug
          role
          title
          typeOfPosition
          location
        }
      }
      press: allDatoCmsPress(sort: { fields: date, order: DESC }) {
        nodes {
          date(formatString: "MMMM DD, YYYY")
          headline
          externalLink
          media
          relatedFocusAreas {
            title
          }
          relatedResources {
            title
          }
          relatedProjects {
            title
          }
          relatedFocusAreas {
            title
          }
        }
      }
      update: allDatoCmsUpdate(sort: { fields: datePosted, order: DESC }) {
        nodes {
          introText
          thematicTags
          structuralTags
          relatedFocusAreas {
            title
          }
          relatedProjects {
            title
          }
          title
          slug
          datePosted(formatString: "MMMM DD, YYYY")
          featuredImage {
            gatsbyImageData(
              placeholder: BLURRED
              aspectRatio: 0.77
              height: 370
              outputPixelDensities: 2
            )
          }
        }
      }
      resource: allDatoCmsResource(sort: { fields: datePosted, order: DESC }) {
        nodes {
          title
          slug
          introText
          thematicTags
          typeOfResource
          structuralTags
          reportSubtitle
          datePosted(formatString: "MMMM DD, YYYY")
          relatedFocusAreas {
            title
          }
          relatedProjects {
            title
          }
          featuredImage {
            gatsbyImageData(
              placeholder: BLURRED
              aspectRatio: 0.77
              height: 370
              outputPixelDensities: 2
            )
          }
        }
      }
    }
  `)

  useEffect(() => {
    // Get the data from the selected content Type
    const singleContentData = allContentData[contentType[0].contentType]?.nodes

    // Filter the data by tag if there is tag
    const filteredSingleContentData = tagToShow
      ? singleContentData?.filter(
          post => post.contentTag.toLowerCase() === tagToShow
        )
      : singleContentData

    const formatedFilteredSingleContentData = filteredSingleContentData.map(
      item => ({
        ...item,
        relatedProjects: item.relatedProjects
          ?.map(item => item.title)
          .join(", "),
        relatedFocusAreas: item.relatedFocusAreas
          ?.map(item => item.title)
          .join(", "),
        relatedResources: item.relatedResources
          ?.map(item => item.title)
          .join(", "),
      })
    )

    // Add data to Component State
    setInitialContentData(formatedFilteredSingleContentData)
    setFilteredSingleContentData(formatedFilteredSingleContentData)

    if (showFilterBar) {
      // Create an object to genereate html select inputs
      const selectInputsValues = contentFilter[0].selectInputs.map(object => {
        if (object.property === "date" || object.property === "datePosted") {
          return {
            id: object.property,
            label: object.inputLabel,
            values: formatedFilteredSingleContentData?.map(
              item => item[object.property]?.split(" ")[2]
            ),
          }
        }
        return {
          id: object.property,
          label: object.inputLabel,
          values: formatedFilteredSingleContentData?.map(item =>
            item[object.property]?.split(", ")
          ),
        }
      })

      // Add to Component State
      setSelectFiltersValues(selectInputsValues)

      if (filters.length > 0) {
        filterData()
      }
    }

    // Reset slice value
    setDataSlice(dataSlice)
  }, [filters])

  // = = = = = = = =
  // Event Handlers

  const filterData = () => {
    const filteredData = initialContentData.filter(item => {
      const filterResult = []

      filters.forEach(filter => {
        for (let key in filter) {
          if (item[key] === undefined || !item[key]?.includes(filter[key])) {
            filterResult.push("false")
          } else {
            filterResult.push("true")
          }
        }
      })

      if (!filterResult.includes("false")) {
        return filterResult
      }
    })

    setFilteredSingleContentData(filteredData)
  }

  const handleClick = e => {
    e.preventDefault()
    const updatedDateSlice = dataSlice + resultsLimit
    setDataSlice(updatedDateSlice)
  }

  return (
    <>
      {showFilterBar && (
        <FilterBar
          filtersValues={selectFiltersValues}
          component={contentFilter[0]}
          filters={filters}
          setFilters={setFilters}
          filterData={filterData}
        />
      )}
      <section
        className={`content-grid content-grid--${contentType[0].contentType}`}
      >
        <div className="container">
          {tagToShow && (
            <div className="row">
              <div className="col-lg-10">
                <h4 className="oblique">{tagToShow}</h4>
              </div>
            </div>
          )}

          <div className="row">
            {cardSelector(
              contentType[0].contentType,
              filteredSingleContentData?.slice(
                0,
                dataSlice === 0 ? filteredSingleContentData.length : dataSlice
              )
            )}
          </div>

          {contentType[0].contentType !== "staff" && (
            <div className="row">
              <div className="col-md-12">
                <div className="load-more-wrapper">
                  {dataSlice <= filteredSingleContentData?.length ? (
                    <button
                      onClick={e => handleClick(e)}
                      className="btn btn--load-more"
                    >
                      Load more results
                    </button>
                  ) : (
                    <p className="notice-text">
                      {filters.length > 0 &&
                      filteredSingleContentData?.length === 0
                        ? `Sorry, there are no articles with your selected options. Try fewer filters?`
                        : `That's all the available articles.`}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  )
}
